import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { MatDialog } from '@angular/material/dialog';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { ActivatedRoute, ParamMap, Router, UrlTree } from '@angular/router';
import { SnackbarService } from '../../services/snackbar.service';
import { LoadingService } from '../../services/loading.service';
import {
  Directions,
  ExtendedSimulationResults, FluidFlowModelDetailDto, FluidFlowModelRangeDto, PcCycle, PcSimulationResults,
  RunSearchFilters, SimulationFilterResult, SimulationResponse, SimulatorConfig,
} from '../../core/models/simulation.model';
import { concatMap, debounceTime, filter, first, forkJoin, map, merge, Observable, of, tap } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { RunFilterOptionsPopupComponent } from '../popups/run-filter-options-popup/run-filter-options-popup.component';
import { DEFAULT_DEBOUNCE_TIME } from '../../core/consts/consts';
import Highcharts from 'highcharts';
import { createBaseLinearChartOptions, createLineSeries, updateChartSeriesVisibilitySmooth } from '../../utils/chart/chart-config.util';
import { RemoveExtensionPipe } from '../pipes/remove-extension.pipe';
import { sanitizeIdentifier, sanitizeSearchString, sanitizeSimulationName } from '../../utils/sanitize.util';

enum SortColumnNameMap {
  name = 'Name',
  fileName = 'File',
  direction = 'Direction',
  cycle = 'Cycle',
  interfactialTension = 'InterfacialTension',
  contactAnglePrimaryDrainage = 'PrimaryDrainage',
  contactAngleFirstImbibition = 'FirstImbibition',
}

@Component({
  selector: 'app-pc-based-simulator',
  imports: [
    CommonModule,
    FormsModule,
    HighchartsChartModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatOption,
    MatSelect,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatRadioModule,
    ReactiveFormsModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './pc-based-simulator.component.html',
  styleUrl: './pc-based-simulator.component.scss',
  standalone: true,
})
export class PcBasedSimulatorComponent extends Destroyable(Object) implements OnInit, AfterViewInit {
  @Input() config!: SimulatorConfig;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly primaryDrainageColor: string = '#4F81BD';
  readonly firstImbibitionColor: string = '#C0504D';
  readonly secondaryDrainageColor: string = '#8064A2';
  readonly displayedColumns: string[] = [
    'select',
    'name',
    'fileName',
    'direction',
    'cycle',
    'interfactialTension',
    'contactAnglePrimaryDrainage',
    'contactAngleFirstImbibition',
  ];
  readonly searchByOptions: { label: string; value: string }[] = [
    { label: 'Name', value: 'Name' },
    { label: 'Rock file', value: 'File' },
  ];

  filterByOptionSelected: string = 'Name';
  searchString: string = '';
  dataSource = new MatTableDataSource<ExtendedSimulationResults>([]);
  totalCount: number = 0;
  filterData!: RunSearchFilters;
  selectedSimulationId: string | null = null;
  selectedRun: ExtendedSimulationResults | null = null;
  selectedSimulation: SimulationResponse<PcSimulationResults> | null = null;
  cyclesForm!: FormGroup;
  simulationNameForm!: FormGroup;
  chart?: Highcharts.Chart;
  chartOptions!: Highcharts.Options;
  isChartCreated: boolean = false;
  isLoading: boolean = false;
  fluidFlowModelsMap: Map<string, FluidFlowModelDetailDto> = new Map();

  constructor(
    private readonly fb: FormBuilder,
    private readonly simulationsService: SimulationsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly dialog: MatDialog,
  ) {
    super();
  }

  get componentId(): SimulatorType {
    return this.config.simulatorType;
  }

  get simulatorTypeToFilter(): SimulatorType {
    // Determine if this is an upscaling or subsample simulator
    const isUpscaling = this.config.simulatorType.startsWith('Upscaling');
    return isUpscaling ? SimulatorType.UpscalingPC : SimulatorType.SubsamplePC;
  }

  get isUpscaling(): boolean {
    return this.config.simulatorType.startsWith('Upscaling');
  }

  get simulationTitle(): string {
    return this.config.title.split(' ').slice(0, 2).join(' ');
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  get simulationName(): string {
    const { name } = this.simulationNameForm.getRawValue();
    return name ?? '';
  }

  get resultForSelectedSimulation(): PcSimulationResults | undefined {
    return this.selectedSimulation?.results?.[0];
  }

  get swd1Value(): string[] {
    return this.resultForSelectedSimulation?.result?.swD1 ?? [];
  }

  get pcd1Value(): string[] {
    return this.resultForSelectedSimulation?.result?.pcD1 ?? [];
  }

  get swImbValue(): string[] {
    return this.resultForSelectedSimulation?.result?.swImb ?? [];
  }

  get pcImbValue(): string[] {
    return this.resultForSelectedSimulation?.result?.pcImb ?? [];
  }

  get swd2Value(): string[] {
    return this.resultForSelectedSimulation?.result?.swD2 ?? [];
  }

  get pcd2Value(): string[] {
    return this.resultForSelectedSimulation?.result?.pcD2 ?? [];
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initSimulationNameForm();
    this.initCyclesForm();

    this.route.queryParamMap.pipe(
      first(),
      concatMap((data: ParamMap): Observable<SimulationFilterResult<ExtendedSimulationResults>> => {
        this.filterData = {
          filterBy: sanitizeIdentifier(data.get('FilterBy')) || this.filterByOptionSelected,
          searchString: sanitizeSearchString(data.get('SearchString')),
          type: this.simulatorTypeToFilter,
          direction: data.getAll('Direction').map(sanitizeIdentifier) || [],
          cycles: data.getAll('Cycles').map(sanitizeIdentifier) || [],
          firstImbibitionMax: this.setRangesFromParamMap(data, 'FirstImbibition.Max'),
          firstImbibitionMin: this.setRangesFromParamMap(data, 'FirstImbibition.Min'),
          interfacialTensionMax: this.setRangesFromParamMap(data, 'InterfacialTension.Max'),
          interfacialTensionMin: this.setRangesFromParamMap(data, 'InterfacialTension.Min'),
          primaryDrainageMax: this.setRangesFromParamMap(data, 'PrimaryDrainage.Max'),
          primaryDrainageMin: this.setRangesFromParamMap(data, 'PrimaryDrainage.Min'),
          sortDirection: sanitizeIdentifier(data.get('SortDirection')) || null,
          sortColumnName: sanitizeIdentifier(data.get('SortColumnName')) || null,
          page: Number(data.get('page')) || 1,
          pageSize: Number(data.get('pageSize')) || 10,
        };

        this.updateQueryParams(this.filterData);
        this.searchString = this.filterData.searchString;
        this.filterByOptionSelected = this.filterData.filterBy;

        return this.loadAvailablePcSimulations(this.filterData);
      }),
      this.takeUntilDestroyed(),
    )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.setupPaginationChangeListener();
    this.setupSortingChangeListener();
  }

  getCycleControl(cycle: PcCycle): FormControl {
    return this.cyclesForm.get(cycle) as FormControl;
  }

  isRunResultValid(result: ExtendedSimulationResults): boolean {
    return result.status.toLowerCase() === 'success';
  }

  isCycleResultExists(cycle: PcCycle): boolean {
    if (!this.resultForSelectedSimulation?.result) return false;

    switch (cycle) {
      case 'd1':
        // Check if data exists - parameters might be null for upscaling simulations
        return Boolean(this.pcd1Value.length && this.swd1Value.length);

      case 'imb':
        // Check if data exists - parameters might be null for upscaling simulations
        return Boolean(this.pcImbValue.length && this.swImbValue.length);

      case 'd2':
        // Secondary Drainage is not supported/loaded
        return false;
    }
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    // Initialize chart with all series upfront for smooth visibility toggling
    this.initializeChartSeries();
  }

  private initializeChartSeries(): void {
    if (!this.chart) return;

    // Clear all existing series before adding new ones
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    const allSeries = this.initChartSeriesForSelectedSimulation();

    // Add all series to the chart upfront
    allSeries.forEach(series => {
      this.chart!.addSeries(series, false);
    });

    this.chart.redraw();

    // Apply initial visibility based on form state
    this.updateChartSeriesVisibility();
  }

  onPcSimulationSelectionChange(run: ExtendedSimulationResults): void {
    this.selectedSimulationId = run.simulationId;
    this.selectedRun = run;

    this.loadingService.startLoading(this.componentId);
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService.getSimulationById(this.selectedSimulationId, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((response: SimulationResponse<PcSimulationResults>) => {
        this.selectedSimulation = {
          ...response,
          results: response.results.filter((result: PcSimulationResults) => result.direction.toLowerCase() === run.direction.toLowerCase()),
        };

        this.configureCyclesFormControls();
        if (this.isChartCreated) {
          this.initializeChartSeries();
        } else {
          this.initChart();
          this.isChartCreated = true;
        }
      });
  }

  onSearch(): void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.applySearch();
  }

  onClearSearch(): void {
    this.searchString = '';
    this.onSearch();
  }

  onSearchStringChange(): void {
    if (!this.searchString) {
      this.onClearSearch();
    }
  }

  onSort(sortColumn: string, sortDirection: string): void {
    const sortColumnName = SortColumnNameMap[sortColumn as keyof typeof SortColumnNameMap];

    if (sortDirection?.length) {
      this.filterData.sortColumnName = sortColumnName;
      this.filterData.sortDirection = sortDirection.toLowerCase() === 'asc' ? 'Asc' : 'Desc';
    } else {
      this.filterData.sortColumnName = 'NoSort';
      this.filterData.sortDirection = null;
    }

    this.onSearch();
  }

  openAdvancedFiltersPopup(): void {
    const dialogRef = this.dialog.open(RunFilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed(),
      )
      .subscribe((data: RunSearchFilters) => {
        this.filterData = data;
        this.onSearch();
      });
  }

  applySearch(): void {
    this.filterData = {
      ...this.filterData,
      filterBy: this.filterByOptionSelected,
      searchString: this.searchString,
      type: this.simulatorTypeToFilter,
      page: this.paginator.pageIndex + 1,
      pageSize: this.paginator.pageSize,
    };

    this.updateQueryParams(this.filterData);
    this.loadAvailablePcSimulations(this.filterData).subscribe();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      this.loadingService.startLoading(this.componentId);

      const directions: Directions = {
        xDirection: this.resultForSelectedSimulation!.direction.toLowerCase() === 'x',
        yDirection: this.resultForSelectedSimulation!.direction.toLowerCase() === 'y',
        zDirection: this.resultForSelectedSimulation!.direction.toLowerCase() === 'z',
      };
      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: this.generateParams(),
      };

      this.simulationsService
        .addSimulation(directions, this.selectedSimulation!.fileName, options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });

          this.router.navigate(['/dashboard']);
        });
    }
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.selectedRun &&
      this.selectedSimulation &&
      this.checkIfAtLeastOneContactAngleSelected() &&
      this.simulationNameForm.valid,
    );
  }

  getCyclesForRun(run: ExtendedSimulationResults): string {
    const cycles: string[] = [];
    if (run.parameters.simulatePrimaryDrainage) {
      cycles.push('PD');
    }
    if (run.parameters.simulateFirstImbibition) {
      cycles.push('FI');
    }
    if (run.parameters.simulateSecondaryDrainage) {
      cycles.push('SD');
    }
    return cycles.join('/');
  }

  private setRangesFromParamMap(data: ParamMap, parameterKey: string): number | null {
    return data.get(parameterKey) != null ? Number(data.get(parameterKey)) : null;
  }

  private checkIfAtLeastOneContactAngleSelected(): boolean {
    return Object.values(this.cyclesForm.getRawValue()).some(Boolean);
  }

  private generateParams(): HttpParams {
    let interfactialTension: number | undefined;
    let contactAnglePrimaryDrainage: number | undefined;
    let contactAngleFirstImbibition: number | undefined;
    let contactAngleSecondaryDrainage: number | undefined;

    if (this.isUpscaling) {
      const model = this.fluidFlowModelsMap.get(this.selectedRun!.parameters?.fluidFlowModelId ?? '');
      if (model) {
        const enabledPhases = this.getEnabledPhases(model);
        const pdPhase = enabledPhases.find(
          p => p.capillaryPressure?.contactAnglePrimaryDrainage != null &&
               p.capillaryPressure.contactAnglePrimaryDrainage !== 0,
        );
        contactAnglePrimaryDrainage = pdPhase?.capillaryPressure?.contactAnglePrimaryDrainage;

        const allHaveFI = enabledPhases.length > 0 && enabledPhases.every(
          p => p.capillaryPressure?.contactAngleFirstImbibition != null &&
               p.capillaryPressure.contactAngleFirstImbibition !== 0,
        );
        if (allHaveFI) {
          contactAngleFirstImbibition = enabledPhases[0].capillaryPressure?.contactAngleFirstImbibition;
        }

        const sdPhase = enabledPhases.find(
          p => p.capillaryPressure?.contactAngleSecondaryDrainage != null &&
               p.capillaryPressure.contactAngleSecondaryDrainage !== 0,
        );
        contactAngleSecondaryDrainage = sdPhase?.capillaryPressure?.contactAngleSecondaryDrainage;

        const itPhase = enabledPhases.find(
          p => p.capillaryPressure?.interfactialTension != null &&
               p.capillaryPressure.interfactialTension !== 0,
        );
        interfactialTension = itPhase?.capillaryPressure?.interfactialTension;
      }
    } else {
      ({
        interfactialTension,
        contactAnglePrimaryDrainage,
        contactAngleFirstImbibition,
        contactAngleSecondaryDrainage,
      } = this.selectedRun!.parameters);
    }

    let httpParams: HttpParams = new HttpParams()
      .set('simulatePrimaryDrainage', false)
      .set('simulateFirstImbibition', false)
      .set('simulateSecondaryDrainage', false)
      .set('name', sanitizeSimulationName(this.simulationName))
      .set('parentSimulationId', this.selectedSimulation!.id);

    // Only set interfacial tension if not null
    if (interfactialTension != null) {
      httpParams = httpParams.set('interfactialTension', interfactialTension);
    }

    if (this.getCycleControl('d1').value && contactAnglePrimaryDrainage != null) {
      httpParams = httpParams
        .set('contactAnglePrimaryDrainage', contactAnglePrimaryDrainage)
        .set('simulatePrimaryDrainage', true);
    }

    if (this.getCycleControl('imb').value && contactAngleFirstImbibition != null) {
      httpParams = httpParams
        .set('contactAngleFirstImbibition', contactAngleFirstImbibition)
        .set('simulateFirstImbibition', true);
    }

    if (this.getCycleControl('d2').value && contactAngleSecondaryDrainage != null) {
      httpParams = httpParams
        .set('contactAngleSecondaryDrainage', contactAngleSecondaryDrainage)
        .set('simulateSecondaryDrainage', true);
    }

    return httpParams;
  }

  private updateQueryParams(filters: RunSearchFilters): void {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url);

    urlTree.queryParams['FilterBy'] = filters.filterBy;
    urlTree.queryParams['SearchString'] = filters.searchString;
    urlTree.queryParams['Direction'] = filters.direction;
    urlTree.queryParams['Cycles'] = filters.cycles;
    urlTree.queryParams['InterfacialTension.Min'] = filters.interfacialTensionMin;
    urlTree.queryParams['InterfacialTension.Max'] = filters.interfacialTensionMax;
    urlTree.queryParams['PrimaryDrainage.Min'] = filters.primaryDrainageMin;
    urlTree.queryParams['PrimaryDrainage.Max'] = filters.primaryDrainageMax;
    urlTree.queryParams['FirstImbibition.Min'] = filters.firstImbibitionMin;
    urlTree.queryParams['FirstImbibition.Max'] = filters.firstImbibitionMax;
    urlTree.queryParams['SortColumnName'] = filters.sortColumnName;
    urlTree.queryParams['SortDirection'] = filters.sortDirection;
    urlTree.queryParams['page'] = filters.page;
    urlTree.queryParams['pageSize'] = filters.pageSize;

    Object.keys(urlTree.queryParams).forEach(key => {
      if (urlTree.queryParams[key] == null || urlTree.queryParams[key] === '') {
        delete urlTree.queryParams[key];
      }
    });

    this.router.navigateByUrl(urlTree);
  }

  private loadAvailablePcSimulations(filters: RunSearchFilters): Observable<SimulationFilterResult<ExtendedSimulationResults>> {
    return this.simulationsService
      .getFilteredRuns(filters)
      .pipe(
        tap((result: SimulationFilterResult<ExtendedSimulationResults>) => {
          this.dataSource.data = result.data;
          this.totalCount = result.count;
        }),
        concatMap((result: SimulationFilterResult<ExtendedSimulationResults>) => {
          if (!this.isUpscaling) return of(result);

          // Collect unique (parentSimulationId, fluidFlowModelId) pairs from the current page
          const uniquePairs = [
            ...new Map(
              result.data
                .filter(run => run.parameters?.fluidFlowModelId && run.parameters?.parentSimulationId)
                .map(run => [
                  run.parameters.fluidFlowModelId!,
                  { simId: run.parameters.parentSimulationId!, modelId: run.parameters.fluidFlowModelId! },
                ])
            ).values(),
          ];

          if (!uniquePairs.length) return of(result);

          return forkJoin(
            uniquePairs.map(({ simId, modelId }) =>
              this.simulationsService.getFluidFlowModelById(simId, modelId)
            )
          ).pipe(
            tap((details: FluidFlowModelDetailDto[]) => {
              this.fluidFlowModelsMap = new Map();
              details.forEach((detail) => {
                this.fluidFlowModelsMap.set(detail.id, detail);
              });
            }),
            map(() => result),
          );
        }),
        this.takeUntilDestroyed(),
      );
  }

  private configureCyclesFormControls(): void {
    if (this.selectedSimulation) {
      (['d1', 'imb', 'd2'] as PcCycle[]).forEach((cycle: PcCycle) => {
        const cycleResultExists: boolean = this.isCycleResultExists(cycle as PcCycle);

        if (cycleResultExists) {
          this.getCycleControl(cycle).patchValue(true, { emitEvent: false });
          this.getCycleControl(cycle).disable({ emitEvent: false });
        } else {
          this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
          this.getCycleControl(cycle).disable({ emitEvent: false });
        }
      });
    } else {
      (['d1', 'imb', 'd2'] as PcCycle[]).forEach((cycle: PcCycle) => {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
      });
    }
  }

  private initChart(): void {
    // Initialize chart with empty series - series will be added dynamically via updateChartSeriesVisibility
    this.chartOptions = createBaseLinearChartOptions({
      title: 'Capillary Pressure (Pc)',
      xAxisTitle: 'Saturation (fraction)',
      yAxisTitle: 'Capillary Pressure (psi)',
      xMin: 0,
      xMax: 1,
      series: [], // Start with empty series
      tooltipFormatter: function() {
        const xVal = typeof this.x === 'number' ? this.x : 0;
        const yVal = typeof this.y === 'number' ? this.y : 0;
        return `<b>${this.series.name}</b><br/>Saturation: ${xVal.toFixed(2)}<br/>Capillary pressure: ${yVal.toFixed(2)} psi`;
      },
    });

    // Set tick intervals for PC chart
    if (this.chartOptions!.xAxis && Array.isArray(this.chartOptions!.xAxis)) {
      (this.chartOptions!.xAxis as any)[0].tickInterval = 0.1;
      (this.chartOptions!.xAxis as any)[0].minorTickInterval = 0.1;
    } else if (this.chartOptions!.xAxis && typeof this.chartOptions!.xAxis === 'object') {
      (this.chartOptions!.xAxis as any).tickInterval = 0.1;
      (this.chartOptions!.xAxis as any).minorTickInterval = 0.1;
    }

    // Override exporting to be enabled for this component
    this.chartOptions!.exporting = {
      enabled: true,
      fallbackToExportServer: false,
      buttons: {
        contextButton: {
          menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
        },
      },
    };
  }

  private initChartSeriesForSelectedSimulation(): Highcharts.SeriesOptionsType[] {
    if (!this.selectedSimulation || !this.resultForSelectedSimulation) return [];

    // Generate ALL possible cycles in consistent order
    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];
    const allSeries: Highcharts.SeriesOptionsType[] = [];

    cycleOrder.forEach((cycle) => {
      const series: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType(this.resultForSelectedSimulation!, cycle);
      allSeries.push(series);
    });

    return allSeries;
  }

  private updateSeriesWhenSelectedSimulationChanged(): void {
    if (!this.chart) return;
    this.updateChartSeriesVisibility();
  }

  private updateSeriesWhenSelectedCyclesChanged(cycle: PcCycle, checked: boolean): void {
    if (!this.chart) return;
    this.updateChartSeriesVisibility();
  }

  private updateChartSeriesVisibility(): void {
    if (!this.chart) return;

    // Determine which series should be visible based on selection
    const selectedSeriesNames = new Set<string>();
    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];
    cycleOrder.forEach((cycle) => {
      if (this.getCycleControl(cycle).value) {
        const series = this.createChartSeriesByCycleType(this.resultForSelectedSimulation!, cycle);
        selectedSeriesNames.add(series.name as string);
      }
    });

    updateChartSeriesVisibilitySmooth(this.chart, selectedSeriesNames);
  }

  private createChartSeriesByCycleType(result: PcSimulationResults, cycle: PcCycle): Highcharts.SeriesOptionsType {
    let data: number[][];
    let name: string;

    switch (cycle) {
      case 'd1':
        name = `Primary drainage (${result.direction.toUpperCase()})`;
        data = this.swd1Value.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcd1Value;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;

      case 'imb':
        name = `First imbibition (${result.direction.toUpperCase()})`;
        data = this.swImbValue.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcImbValue;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;

      case 'd2':
        name = `Secondary drainage (${result.direction.toUpperCase()})`;
        data = this.swd2Value.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcd2Value;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;
    }

    return createLineSeries({
      name,
      data,
      color: this.defineColorForCycleType(cycle),
      markerEnabled: true,
      custom: {
        direction: result.direction.toLowerCase(),
        cycle,
      },
    });
  }

  private defineColorForCycleType(cycle: PcCycle): string {
    switch (cycle) {
      case 'd1':
        return this.primaryDrainageColor;

      case 'imb':
        return this.firstImbibitionColor;

      case 'd2':
        return this.secondaryDrainageColor;
    }
  }

  private setupPaginationChangeListener(): void {
    this.paginator.page.pipe(
      debounceTime(DEFAULT_DEBOUNCE_TIME),
      tap(() => this.applySearch()),
    ).subscribe();
  }

  private setupSortingChangeListener(): void {
    if (this.filterData.sortColumnName && this.filterData.sortColumnName !== 'NoSort' && this.filterData.sortDirection) {
      const sortColumn: string = (Object.keys(SortColumnNameMap) as Array<keyof typeof SortColumnNameMap>)
        .find((k: keyof typeof SortColumnNameMap) => SortColumnNameMap[k] === this.filterData.sortColumnName)!;
      const sortDirection: SortDirection = this.filterData.sortDirection!.toLowerCase() as SortDirection;

      setTimeout(() => {
        this.sort.active = sortColumn;
        this.sort.direction = sortDirection;
        this.sort.sortChange.emit({ active: sortColumn, direction: sortDirection });
      });
    }

    this.sort.sortChange
      .pipe(debounceTime(DEFAULT_DEBOUNCE_TIME))
      .subscribe((sort: Sort) => {
        this.onSort(sort.active, sort.direction);
      });
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', Validators.maxLength(256)],
    });
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      d1: [{ value: false, disabled: true }],
      imb: [{ value: false, disabled: true }],
      d2: [{ value: false, disabled: true }],
    });

    merge(
      this.getCycleControl('d1').valueChanges.pipe(map(value => ({ cycle: 'd1', value }))),
      this.getCycleControl('imb').valueChanges.pipe(map(value => ({ cycle: 'imb', value }))),
      this.getCycleControl('d2').valueChanges.pipe(map(value => ({ cycle: 'd2', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ cycle, value }: { cycle: string, value: boolean }) => {
        this.updateSeriesWhenSelectedCyclesChanged(cycle as PcCycle, value);
      });
  }

  getUpscalingCycles(run: ExtendedSimulationResults): string {
    const model = this.fluidFlowModelsMap.get(run.parameters?.fluidFlowModelId ?? '');
    if (!model) return '—';
    const enabledPhases = this.getEnabledPhases(model);
    const cycles: string[] = [];
    const hasPD = enabledPhases.some(
      p => p.capillaryPressure?.contactAnglePrimaryDrainage != null &&
           p.capillaryPressure.contactAnglePrimaryDrainage !== 0
    );
    const hasFI = enabledPhases.length > 0 && enabledPhases.every(
      p => p.capillaryPressure?.contactAngleFirstImbibition != null &&
           p.capillaryPressure.contactAngleFirstImbibition !== 0
    );
    if (hasPD) cycles.push('PD');
    if (hasFI) cycles.push('FI');
    return cycles.join('/') || '—';
  }

  getUpscalingInterfacialTension(run: ExtendedSimulationResults): string {
    const model = this.fluidFlowModelsMap.get(run.parameters?.fluidFlowModelId ?? '');
    if (!model) return '—';
    const enabledPhases = this.getEnabledPhases(model);
    const values = enabledPhases
      .map(p => p.capillaryPressure?.interfactialTension)
      .filter((v): v is number => v != null && v !== 0);
    if (!values.length) return 'Not Available';
    const min = Math.min(...values);
    const max = Math.max(...values);
    return this.formatFluidFlowRange({ min, max });
  }

  getUpscalingPrimaryDrainage(run: ExtendedSimulationResults): string {
    const model = this.fluidFlowModelsMap.get(run.parameters?.fluidFlowModelId ?? '');
    if (!model) return '—';
    const enabledPhases = this.getEnabledPhases(model);
    const values = enabledPhases
      .map(p => p.capillaryPressure?.contactAnglePrimaryDrainage)
      .filter((v): v is number => v != null && v !== 0);
    if (!values.length) return 'Not Available';
    const min = Math.min(...values);
    const max = Math.max(...values);
    return this.formatFluidFlowRange({ min, max });
  }

  getUpscalingFirstImbibition(run: ExtendedSimulationResults): string {
    const model = this.fluidFlowModelsMap.get(run.parameters?.fluidFlowModelId ?? '');
    if (!model) return '—';
    const enabledPhases = this.getEnabledPhases(model);
    const hasFI = enabledPhases.length > 0 && enabledPhases.every(
      p => p.capillaryPressure?.contactAngleFirstImbibition != null &&
           p.capillaryPressure.contactAngleFirstImbibition !== 0
    );
    if (!hasFI) return 'Not Selected';
    const values = enabledPhases
      .map(p => p.capillaryPressure!.contactAngleFirstImbibition)
      .filter((v): v is number => v != null);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return this.formatFluidFlowRange({ min, max });
  }

  private getEnabledPhases(model: FluidFlowModelDetailDto) {
    return (model.phasesDefinition ?? []).filter(
      p => p.phasePorosity !== 0 && p.phasePorosity !== 1
    );
  }

  private formatFluidFlowRange(range: FluidFlowModelRangeDto | undefined): string {
    if (!range) return '—';
    const { min, max } = range;
    if (min === 0 && max === 0) return 'Not Available';
    return `${min.toFixed(3)} to ${max.toFixed(3)}`;
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading: boolean) => {
        this.isLoading = isLoading;
      });
  }
}
